import React, { useMemo } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import { resolvePath } from '@/utils/resolvePath';

const GoogleReviews = (props: any) => {
    const { garage, reviewData } = props;
    const { reviews = [] } = props?.reviewsData || {};
    const { averageRating = 5, userRatingsTotal = 100 } = reviewData;
    const Star = ({ fill }: { fill: number }) => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={32}
            height={32}
            viewBox="0 0 32 32"
            fill="none"
            style={{
                transform: 'rotate(180deg)',
            }}
        >
            <defs>
                <linearGradient
                    id={`grad${fill}`}
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                >
                    <stop
                        offset={`${100 - fill}%`}
                        style={{ stopColor: '#E0E0E0', stopOpacity: 1 }}
                    />
                    <stop
                        offset={`${100 - fill}%`}
                        style={{ stopColor: '#FFC400', stopOpacity: 1 }}
                    />
                </linearGradient>
            </defs>
            <path
                d="M28.2526 15.8186C29.7725 14.6571 28.9511 12.2295 27.0382 12.2295H22.2054C21.328 12.2295 20.553 11.6576 20.2943 10.8193L18.8632 6.18232C18.2831 4.30294 15.6233 4.30177 15.0416 6.18064L13.6054 10.8195C13.3459 11.6577 12.5703 12.2288 11.6929 12.228L6.86798 12.2231C4.95427 12.2211 4.13093 14.6496 5.65122 15.8119L9.73648 18.9352C10.4026 19.4445 10.6803 20.3152 10.4321 21.1161L8.91266 26.019C8.33611 27.8793 10.4883 29.3825 12.0365 28.2008L15.7409 25.3733C16.4578 24.8261 17.4522 24.8264 18.1688 25.374L21.8668 28.2C23.414 29.3824 25.5668 27.881 24.992 26.0205L23.4767 21.1155C23.2293 20.3149 23.5073 19.4449 24.1732 18.9361L28.2526 15.8186Z"
                fill={`url(#grad${fill})`}
            />
        </svg>
    );

    const starsValue = () => {
        const fullStars = Math.floor(averageRating);
        const partialStar = averageRating - fullStars;
        const emptyStars = 5 - Math.ceil(averageRating);
        let stars = [];
        {
            Array.from({ length: fullStars }).map((_, index) =>
                stars.push(<Star key={`full-${index}`} fill={100} />)
            );
        }
        {
            partialStar > 0 && stars.push(<Star fill={partialStar * 100} />);
        }
        {
            Array.from({ length: emptyStars }).map((_, index) =>
                stars.push(<Star key={`empty-${index}`} fill={0} />)
            );
        }
        return stars;
    };

    const colors = ['#D0BDF4', '#F3C0BE', '#FFE380'];

    const reviewsJSONLd = useMemo(
        () => ({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: averageRating,
                reviewCount: userRatingsTotal,
            },
            name: `${garage}`,
            review: reviews?.map((review: any, i: number) => ({
                '@type': 'Review',
                author: review.reviewer_name,
                datePublished: review.review_date,
                reviewBody: review.review_text,
                name: `Experience at ${garage}`,
                reviewRating: {
                    '@type': 'Rating',
                    bestRating: '5',
                    ratingValue: '5',
                    worstRating: '1',
                },
            })),
        }),
        []
    );

    return (
        <>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(reviewsJSONLd),
                }}
            />
            <GoogleReviewsContainer>
                <GoogleReviewsWrapper>
                    <GoogleReviewsHeadingWrapper>
                        {' '}
                        <GoogleReviewsHeadingContainer>
                            <Image
                                src={resolvePath('/images/seo/Tool.svg')}
                                width={17}
                                height={12}
                            />
                            <GoogleReviewsHeading>
                                {'Join '}
                                <GoogleReviewValue>10k+</GoogleReviewValue>
                                {' satisfied customers'}
                            </GoogleReviewsHeading>
                            <Image
                                src={resolvePath('/images/seo/Tool.svg')}
                                width={17}
                                height={12}
                                style={{ transform: 'rotate(180deg)' }}
                            />
                        </GoogleReviewsHeadingContainer>
                    </GoogleReviewsHeadingWrapper>
                    <GoogleStarsWrapper>
                        <GoogleStarsContainer>
                            <Image
                                src={resolvePath('/images/seo/GoogleIcon.svg')}
                                width={32}
                                height={32}
                            />
                            <Line />
                            <Rating>{averageRating}</Rating>
                            {starsValue()}
                        </GoogleStarsContainer>
                    </GoogleStarsWrapper>
                </GoogleReviewsWrapper>
                <ReviewsWrapper>
                    {reviews?.map((review: any) => (
                        <ReviewWrapper>
                            <ReviewContainer>
                                <Image
                                    src={resolvePath('/images/seo/Quote.svg')}
                                    width={17}
                                    height={44}
                                />
                                <ReviewText>{review.review_text}</ReviewText>
                            </ReviewContainer>
                            <WaveWrapper>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="242"
                                    height="100"
                                    viewBox="0 0 242 100"
                                    fill="none"
                                >
                                    <path
                                        d="M0 15.9045C0 15.9045 38 -12.6922 114.5 6.80532C191 26.3028 242 15.9045 242 15.9045V100H0V15.9045Z"
                                        fill={`${colors[Math.floor(Math.random() * colors.length)]}`}
                                    />
                                </svg>
                                <Avatar>
                                    <Image
                                        src={review.reviewer_img_url}
                                        width={48}
                                        height={48}
                                    />
                                </Avatar>
                                <UserDataWrapper>
                                    <Name>{review.reviewer_name}</Name>
                                    <Date>{review.review_date}</Date>
                                </UserDataWrapper>
                            </WaveWrapper>
                        </ReviewWrapper>
                    ))}
                </ReviewsWrapper>
            </GoogleReviewsContainer>
        </>
    );
};

export default GoogleReviews;

const GoogleReviewsContainer = styled.div`
    padding: 20px 16px;
`;
const GoogleReviewsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    flex-shrink: 0;
`;
const GoogleReviewsHeadingWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 12px;
    align-self: stretch;
`;

const GoogleReviewsHeadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const GoogleReviewsHeading = styled.div`
    color: #4b4b4b;
    text-align: center;
    font-family: 'Euclid Circular B';
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
`;

const GoogleReviewValue = styled.span`
    color: #5920c5;
`;

const GoogleStarsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 12px;
    align-self: stretch;
`;
const GoogleStarsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
`;

const Line = styled.div`
    width: 1px;
    height: 32px;
    background: #e8e8e8;
`;

const Rating = styled.div`
    display: flex;
    width: 45.949px;
    height: 32px;
    flex-direction: column;
    justify-content: center;
    color: #121212;
    font-family: 'Euclid Circular B';
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: 40px;
    letter-spacing: -0.1px;
`;

const ReviewsWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-shrink: 0;
    overflow: scroll;
    padding-top: 28px;
    scrollbar-width: none;
`;

const ReviewWrapper = styled.div`
    position: relative;
    display: flex;
    width: 210px;
    padding: 16px 16px 132px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    flex-shrink: 0;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0px 4px 10px 4px rgba(54, 53, 76, 0.08);
    overflow: hidden;
`;
const ReviewContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: -8px;
    align-self: stretch;
`;
const ReviewText = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    color: #121212;
    font-family: 'Euclid Circular B';
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
`;
const WaveWrapper = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    height: 100px;
`;

const Avatar = styled.div`
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: white;
    overflow: hidden;
    border: 1px solid white;
`;

const UserDataWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    position: absolute;
    top: 36px;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
`;

const Name = styled.div`
    color: #121212;
    font-family: 'Euclid Circular B';
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
`;

const Date = styled.div`
    color: #4b4b4b;
    font-family: 'Euclid Circular B';
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
`;
